import type { IndividualMetric } from '@dashhudson/api-client/dashboard/model/raw';
import type {
  PerformanceAlert,
  PerformanceAlertThresholdItemChannel,
  ResolveBenchmarksRequestBodyChannel,
} from '@dashhudson/api-client/library/model';
import type { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router';

import { formatDynamicDuration, TIME_UNIT_OPTION } from '@dashhudson/dashing-ui/utils/formatters';

import type { CHANNELS as DASHBOARDS_CHANNELS } from '@/models/dashboards/channels.enum';

import { METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import {
  CHANNEL_OPTIONS,
  MEDIA_TYPE_OPTIONS,
  SOURCE_TYPE_LABELS,
  THRESHOLD_PRESET_OPTIONS,
  type ThresholdCategory,
} from '@/app/settings/constants/performance-alerts';
import { getPerformanceAlertContentTagIds } from '@/app/settings/utils/performance-alert-filters';
import { BRAND } from '@/models/auth/permissions.enum';
import { DATA_TYPES } from '@/models/dashboards/metrics.constants';
import { getRouteBrandLabel } from '@/router/brand';
import { useContentTagsStore } from '@/stores/content-tags';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useMetricsStore } from '@/stores/metrics';

export type PerformanceAlertSourceType = 'ORGANIC_ONLY' | 'PROMOTED_ONLY' | 'ORGANIC_AND_PROMOTED';
export type PerformanceAlertMediaType =
  | 'ALL_MEDIA'
  | 'CAROUSEL'
  | 'PHOTO'
  | 'IMAGE'
  | 'REEL'
  | 'VIDEO'
  | 'LIVE'
  | 'ON_DEMAND'
  | 'SHORTS'
  | 'DOCUMENT'
  | 'LINK'
  | 'TEXT'
  | 'AUDIO';

const CHANNEL_SUPPORTED_MEDIA_TYPES: Record<
  PerformanceAlertThresholdItemChannel,
  Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'>[]
> = {
  INSTAGRAM: ['CAROUSEL', 'PHOTO', 'REEL', 'VIDEO'],
  INSTAGRAM_UGC: ['CAROUSEL', 'PHOTO', 'REEL', 'VIDEO'],
  FACEBOOK: ['CAROUSEL', 'PHOTO', 'REEL', 'VIDEO', 'LINK', 'TEXT'],
  INSTAGRAM_STORY: ['PHOTO', 'VIDEO'],
  TIKTOK: ['PHOTO', 'VIDEO'],
  TIKTOK_UGC: ['VIDEO'],
  YOUTUBE: ['LIVE', 'ON_DEMAND', 'SHORTS'],
  PINTEREST: ['CAROUSEL', 'PHOTO', 'VIDEO'],
  LINKEDIN: ['CAROUSEL', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LINK', 'TEXT'],
  THREADS: ['CAROUSEL', 'IMAGE', 'VIDEO', 'TEXT', 'AUDIO'],
  X: ['CAROUSEL', 'PHOTO', 'VIDEO', 'LINK', 'TEXT'],
  SNAPCHAT: ['PHOTO', 'VIDEO'],
};

const DEFAULT_SUPPORTED_MEDIA_TYPES: PerformanceAlertMediaType[] = [
  'ALL_MEDIA',
  'CAROUSEL',
  'PHOTO',
  'REEL',
  'VIDEO',
];

const DEFAULT_MEDIA_TYPE_ES_TOKENS: Partial<Record<PerformanceAlertMediaType, string[]>> = {
  CAROUSEL: ['carousel'],
  PHOTO: ['photo'],
  REEL: ['reel'],
  VIDEO: ['video'],
  LINK: ['link'],
  TEXT: ['text'],
};

const CHANNEL_MEDIA_TYPE_ES_TOKENS: Partial<
  Record<PerformanceAlertThresholdItemChannel, Partial<Record<PerformanceAlertMediaType, string[]>>>
> = {
  INSTAGRAM: { PHOTO: ['image'] },
  INSTAGRAM_UGC: { PHOTO: ['image'] },
  INSTAGRAM_STORY: { PHOTO: ['image'], VIDEO: ['video'] },
  PINTEREST: { PHOTO: ['image'] },
  YOUTUBE: { LIVE: ['live'], ON_DEMAND: ['on_demand'], SHORTS: ['shorts'] },
  LINKEDIN: {
    CAROUSEL: ['multi_image'],
    IMAGE: ['image'],
    VIDEO: ['video'],
    DOCUMENT: ['document'],
    LINK: ['link'],
    TEXT: ['text'],
  },
  THREADS: {
    CAROUSEL: ['carousel_album'],
    IMAGE: ['image'],
    VIDEO: ['video'],
    TEXT: ['text_post'],
    AUDIO: ['audio'],
  },
};

function getMediaTypeEsTokens(
  channel: PerformanceAlertThresholdItemChannel,
  mediaType: Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'>,
): string[] {
  return (
    CHANNEL_MEDIA_TYPE_ES_TOKENS[channel]?.[mediaType] ??
    DEFAULT_MEDIA_TYPE_ES_TOKENS[mediaType] ??
    []
  );
}

export function getDashboardsChannel(
  channel: PerformanceAlertThresholdItemChannel,
): keyof typeof DASHBOARDS_CHANNELS {
  if (channel === 'INSTAGRAM_STORY') {
    return 'INSTAGRAM_STORIES';
  }
  if (channel === 'X') {
    return 'TWITTER';
  }
  return channel;
}

export function getSupportedPerformanceAlertMediaTypes(
  channel?: PerformanceAlertThresholdItemChannel,
): PerformanceAlertMediaType[] {
  if (!channel) return DEFAULT_SUPPORTED_MEDIA_TYPES;
  const channelMediaTypes = CHANNEL_SUPPORTED_MEDIA_TYPES[channel];
  if (!channelMediaTypes?.length) return DEFAULT_SUPPORTED_MEDIA_TYPES;
  if (channelMediaTypes.length === 1) return [...channelMediaTypes];
  return ['ALL_MEDIA', ...channelMediaTypes];
}

export function getPerformanceAlertBenchmarkMediaTypes(
  channel: PerformanceAlertThresholdItemChannel,
  mediaType: PerformanceAlertMediaType,
): string[] {
  const supportedMediaTypes = getSupportedPerformanceAlertMediaTypes(channel).filter(
    (type): type is Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'> => type !== 'ALL_MEDIA',
  );
  if (mediaType === 'ALL_MEDIA') {
    const tokens = supportedMediaTypes.flatMap((type) => getMediaTypeEsTokens(channel, type));
    return [...new Set(tokens)];
  }
  if (!supportedMediaTypes.includes(mediaType)) {
    return getPerformanceAlertBenchmarkMediaTypes(channel, 'ALL_MEDIA');
  }
  return getMediaTypeEsTokens(channel, mediaType);
}

export function inferSourceTypeFromMetric(
  metric: string,
  postTypes?: readonly unknown[],
): PerformanceAlertSourceType {
  if (postTypes?.length) {
    const normalizedPostTypes = new Set(
      postTypes.map((postType) => String(postType).toUpperCase()),
    );
    const hasOrganic =
      normalizedPostTypes.has('ORGANIC') || normalizedPostTypes.has('ESTIMATED_ORGANIC');
    const hasPromoted = normalizedPostTypes.has('PROMOTED') || normalizedPostTypes.has('ADS');
    if (hasOrganic && !hasPromoted) return 'ORGANIC_ONLY';
    if (hasPromoted && !hasOrganic) return 'PROMOTED_ONLY';
    if (hasOrganic && hasPromoted) return 'ORGANIC_AND_PROMOTED';
  }

  const normalizedMetric = metric.toUpperCase();
  if (normalizedMetric.startsWith('ORGANIC_')) return 'ORGANIC_ONLY';
  if (normalizedMetric.startsWith('PAID_') || normalizedMetric.startsWith('PROMOTED_')) {
    return 'PROMOTED_ONLY';
  }
  return 'ORGANIC_AND_PROMOTED';
}

export function inferMediaTypeFromValue(
  mediaTypes: readonly unknown[] | undefined,
  channel: PerformanceAlertThresholdItemChannel,
): PerformanceAlertMediaType | undefined {
  const uniqueTokens = new Set((mediaTypes ?? []).map((token) => String(token).toLowerCase()));

  const supportedMediaTypes = getSupportedPerformanceAlertMediaTypes(channel).filter(
    (mediaType): mediaType is Exclude<PerformanceAlertMediaType, 'ALL_MEDIA'> =>
      mediaType !== 'ALL_MEDIA',
  );

  const matchedMediaTypes = supportedMediaTypes.filter((mediaType) =>
    getMediaTypeEsTokens(channel, mediaType).some((token) => uniqueTokens.has(token)),
  );

  if (matchedMediaTypes.length === 1) return matchedMediaTypes[0];
  if (supportedMediaTypes.length > 0 && matchedMediaTypes.length === supportedMediaTypes.length) {
    return 'ALL_MEDIA';
  }
  return undefined;
}

export function getPerformanceAlertMediaTypeLabel(mediaType?: PerformanceAlertMediaType): string {
  return MEDIA_TYPE_OPTIONS.find((option) => option.value === mediaType)?.label ?? '';
}

export function getPerformanceAlertSourceTypeLabel(sourceType: PerformanceAlertSourceType): string {
  return SOURCE_TYPE_LABELS[sourceType];
}

export function getPerformanceAlertChannelCategory(
  channel: ResolveBenchmarksRequestBodyChannel,
): ThresholdCategory {
  return CHANNEL_OPTIONS.find((option) => option.value === channel)?.category ?? 'OWNED';
}

export function getPerformanceAlertChannelLabel(
  channel: ResolveBenchmarksRequestBodyChannel,
): string {
  const label = CHANNEL_OPTIONS.find((option) => option.value === channel)?.label ?? channel;
  return getPerformanceAlertChannelCategory(channel) === 'UGC' ? `${label} UGC` : label;
}

export function toThresholdPresetPercentile(percentile: number | undefined): 75 | 85 | 95 {
  if (percentile === 85) return 85;
  if (percentile === 95) return 95;
  return 75;
}

export function formatPercentileLabel(percentile: number, isNegative = false): string {
  const preset = THRESHOLD_PRESET_OPTIONS.find((option) => option.percentile === percentile);
  if (!preset?.percentile) return `Top ${100 - percentile}% Benchmark`;
  return `${isNegative ? preset.negativeLabel : preset.label} Benchmark`;
}

const DURATION_RAW_UNIT: Record<string, TIME_UNIT_OPTION> = {
  [DATA_TYPES.SECONDS]: TIME_UNIT_OPTION.SECOND,
  [DATA_TYPES.MILLISECONDS]: TIME_UNIT_OPTION.MILLISECOND,
};

export function isThresholdDurationDataType(dataType?: string): boolean {
  return Object.hasOwn(DURATION_RAW_UNIT, (dataType ?? '').toUpperCase());
}

// Time metrics (data_type SECONDS/MILLISECONDS) render as a duration (e.g. `10m:30s`)
// Non-time metrics (and non-finite values) return null so callers keep their own formatting
export function formatThresholdDurationValue(amount: number, dataType?: string): string | null {
  if (!isThresholdDurationDataType(dataType) || !Number.isFinite(amount)) return null;
  const rawUnit = DURATION_RAW_UNIT[(dataType ?? '').toUpperCase()];
  return formatDynamicDuration(Math.floor(amount), { durationOptions: { rawUnit } });
}

const SECONDS_PER_HOUR = 60 * 60;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;

export interface ThresholdDurationParts {
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export function durationPartsToRawAmount(
  parts: Partial<Record<keyof ThresholdDurationParts, number | string>> | null | undefined,
  dataType?: string,
): number | undefined {
  if (!isThresholdDurationDataType(dataType) || parts == null) return undefined;
  const hours = Number(parts.hours) || 0;
  const minutes = Number(parts.minutes) || 0;
  const seconds = Number(parts.seconds) || 0;
  const totalSeconds = hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
  if (totalSeconds <= 0) return undefined;
  const isMilliseconds = (dataType ?? '').toUpperCase() === DATA_TYPES.MILLISECONDS;
  return isMilliseconds ? totalSeconds * MS_PER_SECOND : totalSeconds;
}

export function rawAmountToDurationParts(
  amount: number | null | undefined,
  dataType?: string,
): ThresholdDurationParts {
  if (!isThresholdDurationDataType(dataType) || amount == null || !Number.isFinite(amount)) {
    return {};
  }
  const isMilliseconds = (dataType ?? '').toUpperCase() === DATA_TYPES.MILLISECONDS;
  const totalSeconds = Math.floor(isMilliseconds ? amount / MS_PER_SECOND : amount);
  if (totalSeconds <= 0) return {};
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return {
    hours: hours || undefined,
    minutes: minutes || undefined,
    seconds: seconds || undefined,
  };
}

function metricMatchesSourceType(
  postTypes: readonly unknown[] | undefined,
  sourceType: PerformanceAlertSourceType,
): boolean {
  const normalized = new Set((postTypes ?? []).map((postType) => String(postType).toUpperCase()));
  const hasOrganic = normalized.has('ORGANIC') || normalized.has('ESTIMATED_ORGANIC');
  const hasPromoted = normalized.has('PROMOTED') || normalized.has('ADS');

  // metrics without post types are source-agnostic totals,
  // so showing them under the combined Organic + Promoted picker only.
  if (normalized.size === 0) {
    return sourceType === 'ORGANIC_AND_PROMOTED';
  }
  if (sourceType === 'ORGANIC_ONLY') {
    return hasOrganic && !hasPromoted;
  }
  if (sourceType === 'PROMOTED_ONLY') {
    return hasPromoted && !hasOrganic;
  }
  return hasOrganic && hasPromoted;
}

const FORCE_ORGANIC_ONLY_CHANNELS = new Set<PerformanceAlertThresholdItemChannel>([
  'INSTAGRAM_UGC',
  'TIKTOK_UGC',
  'INSTAGRAM_STORY',
]);

function normalizeMetricDisplayName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function baseMetricDisplayName(name: string): string {
  return normalizeMetricDisplayName(name)
    .replace(/^ugc\s*-\s*/, '')
    .replace(/\s*\(discontinued\)$/, '')
    .replace(/\s*-\s*(est\.\s*)?(paid|promoted|organic|total)$/, '');
}

// 'active' (default) hides deprecated metrics; 'discontinued' returns ONLY deprecated
// ones so the picker can surface them in a separate section.
export type MetricDeprecationFilter = 'active' | 'discontinued';

// Structural eligibility and per-metric feature-flag gating are enforced by library-backend (the
// single source of truth, which also gates the write path). `metricsByKey` is the already-eligible
// set from the endpoint, so this only applies the client-side presentation filters: the
// deprecated/active split and source-type matching.
export function buildPerformanceAlertMetricOptions(
  metricsByKey: Record<string, IndividualMetric>,
  sourceType: PerformanceAlertSourceType,
  channel?: PerformanceAlertThresholdItemChannel,
  deprecation: MetricDeprecationFilter = 'active',
): Array<{ value: string; label: string }> {
  const forceOrganicOnly = channel ? FORCE_ORGANIC_ONLY_CHANNELS.has(channel) : false;
  const metricOptionsByLabel = Object.entries(metricsByKey)
    .filter(([, details]) => {
      if (!details) return false;
      const isDeprecated = details.deprecated === true;
      if (deprecation === 'discontinued' ? !isDeprecated : isDeprecated) return false;
      if (forceOrganicOnly) {
        if (sourceType !== 'ORGANIC_ONLY') return false;
        if (!details.post_types?.length) return true;
        return metricMatchesSourceType(details.post_types, 'ORGANIC_ONLY');
      }
      return metricMatchesSourceType(details.post_types, sourceType);
    })
    .reduce((byLabel, [metricKey, details]) => {
      const label = details.display_name ?? metricKey;
      if (!byLabel.has(label)) {
        byLabel.set(label, { value: metricKey, label });
      }
      return byLabel;
    }, new Map<string, { value: string; label: string }>());

  return Array.from(metricOptionsByLabel.values())
    .map(({ value, label }) => ({ value, label }))
    .toSorted((left, right) => left.label.localeCompare(right.label));
}

// Meta renamed Facebook "Reach" to "Viewers".
export function formatFacebookReachLabel(
  label: string,
  channel: PerformanceAlertThresholdItemChannel | null | undefined,
): string {
  if (channel !== 'FACEBOOK') return label;
  return label.replace(/^Reach\b/, 'Viewers');
}

export function hasPerformanceAlertActionError(performanceAlert: PerformanceAlert): boolean {
  const actions = performanceAlert.details.actions;

  const contentTagsStore = useContentTagsStore();
  const contentTagIds = actions.contentTags?.map((t) => t.id) ?? [];
  const inaccessibleContentTags =
    contentTagsStore.allOrganizationsContentTags.length > 0
      ? contentTagsStore.filterContentTagsByMissingBrand(performanceAlert.brandIds, contentTagIds)
      : [];

  return Boolean(
    actions.contentTags?.some((t) => t.deleted) ||
    inaccessibleContentTags.length > 0 ||
    actions.boards?.some((b) => b.deleted) ||
    actions.campaigns?.some((c) => c.deleted) ||
    actions.galleries?.some((g) => g.deleted) ||
    actions.email?.some((r) => r.deleted || !r.active),
  );
}

export function hasPerformanceAlertFilterError(performanceAlert: PerformanceAlert): boolean {
  const contentTagIds = getPerformanceAlertContentTagIds(performanceAlert.details.filters);
  const contentTagsStore = useContentTagsStore();
  if (!contentTagsStore.hasLoadedAllOrganizationsContentTags) return false;
  const identityStore = useIdentityStore();
  const accessibleBrandIds = Object.values(identityStore.identity?.brands ?? {})
    .filter(
      (brand) =>
        performanceAlert.brandIds.includes(brand.id) &&
        identityStore.guard(BRAND.TAGS.CAN_ACCESS_CONTENT_TAGS, brand),
    )
    .map((brand) => brand.id);
  const existingTagIds = new Set(
    contentTagsStore.allOrganizationsContentTags.map((tag: { id: number }) => tag.id),
  );
  return (
    contentTagIds.some((id) => !existingTagIds.has(id)) ||
    contentTagsStore.filterContentTagsByMissingBrand(accessibleBrandIds, contentTagIds).length > 0
  );
}

export function hasPerformanceAlertDeprecatedMetric(performanceAlert: PerformanceAlert): boolean {
  const metricsStore = useMetricsStore();
  return performanceAlert.details.thresholds.items.some((threshold) => {
    const channel = getDashboardsChannel(threshold.channel);
    const details = metricsStore.getTargetMetricDetails(
      channel,
      METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT,
      threshold.metric,
    );
    return details?.deprecated === true;
  });
}

export function hasPerformanceAlertError(performanceAlert: PerformanceAlert): boolean {
  return (
    hasPerformanceAlertActionError(performanceAlert) ||
    hasPerformanceAlertFilterError(performanceAlert) ||
    hasPerformanceAlertDeprecatedMetric(performanceAlert)
  );
}

export async function performanceAlertsAccessGuard(
  to: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  const flagStore = useFlagStore();
  const identityStore = useIdentityStore();

  // Current brand is not set until after the navigation is resolved
  // See `useBrandSwitcher.performBrandSwitch`
  const brandLabel = getRouteBrandLabel(to);
  const targetBrand = brandLabel ? identityStore.identity?.brands?.[brandLabel] : null;

  await flagStore.waitForUserFlags;
  const canAccess =
    flagStore.flags.performanceAlerts &&
    targetBrand &&
    identityStore.guard(BRAND.SETTINGS.CAN_ACCESS_CONTENT_AUTOMATION, targetBrand);

  if (canAccess) return true;
  if (!brandLabel) return false;
  return {
    name: 'settings.profile',
    params: { brandLabel },
  };
}
